import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FortuneSign, getRandomSign, getSignById } from "@/data/fortuneData";
import { Shuffle, Search } from "lucide-react";

interface FortuneDrawerProps {
  onSignDrawn: (sign: FortuneSign) => void;
}

export const FortuneDrawer = ({ onSignDrawn }: FortuneDrawerProps) => {
  const [inputSignNumber, setInputSignNumber] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [error, setError] = useState("");

  const handleRandomDraw = async () => {
    setIsDrawing(true);
    setError("");
    
    // 模擬抽簽動畫效果
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const randomSign = getRandomSign();
    onSignDrawn(randomSign);
    setIsDrawing(false);
  };

  const handleManualInput = () => {
    setError("");
    const signNumber = parseInt(inputSignNumber);
    
    if (!inputSignNumber) {
      setError("請輸入簽號");
      return;
    }
    
    if (isNaN(signNumber) || signNumber < 1 || signNumber > 60) {
      setError("請輸入1-60之間的有效簽號");
      return;
    }
    
    const sign = getSignById(signNumber);
    if (sign) {
      onSignDrawn(sign);
      setInputSignNumber("");
    } else {
      setError("找不到該簽號");
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto bg-gradient-to-br from-red-50 to-yellow-50 border-2 border-red-200 shadow-lg">
      <CardHeader className="text-center bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
        <CardTitle className="text-2xl font-bold">黃大仙解簽</CardTitle>
        <p className="text-red-100 mt-2">誠心祈求，必有感應</p>
      </CardHeader>
      
      <CardContent className="p-6">
        <Tabs defaultValue="random" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="random" className="text-lg">隨機抽簽</TabsTrigger>
            <TabsTrigger value="manual" className="text-lg">輸入簽號</TabsTrigger>
          </TabsList>
          
          <TabsContent value="random" className="space-y-6">
            <div className="text-center space-y-4">
              <div className="bg-white p-6 rounded-lg border-2 border-red-200 shadow-inner">
                <h3 className="text-xl font-semibold text-red-800 mb-4">誠心祈求</h3>
                <p className="text-gray-700 mb-4">
                  請在心中默念您想要詢問的問題，然後點擊下方按鈕抽簽
                </p>
                <div className="text-sm text-red-600 space-y-1">
                  <p>• 心誠則靈，請以虔誠之心求簽</p>
                  <p>• 一事一問，不可貪心多求</p>
                  <p>• 簽文僅供參考，命運掌握在自己手中</p>
                </div>
              </div>
              
              <Button 
                onClick={handleRandomDraw}
                disabled={isDrawing}
                size="lg"
                className={`temple-gradient hover:opacity-90 text-white px-8 py-4 text-lg font-semibold temple-shadow smooth-transition ${isDrawing ? 'fortune-drawing' : ''}`}
              >
                <Shuffle className="w-6 h-6 mr-2" />
                {isDrawing ? "正在抽簽..." : "開始抽簽"}
              </Button>
              
              {isDrawing && (
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="manual" className="space-y-6">
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-lg border-2 border-red-200 shadow-inner">
                <h3 className="text-xl font-semibold text-red-800 mb-4">輸入簽號</h3>
                <p className="text-gray-700 mb-4">
                  如果您已經有特定的簽號，請在下方輸入（1-60）
                </p>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="signNumber" className="text-red-700 font-medium">
                      簽號 (1-60)
                    </Label>
                    <Input
                      id="signNumber"
                      type="number"
                      min="1"
                      max="60"
                      value={inputSignNumber}
                      onChange={(e) => setInputSignNumber(e.target.value)}
                      placeholder="請輸入1-60之間的簽號"
                      className="mt-2 border-red-200 focus:border-red-400"
                    />
                  </div>
                  
                  {error && (
                    <p className="text-red-600 text-sm">{error}</p>
                  )}
                  
                  <Button 
                    onClick={handleManualInput}
                    size="lg"
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold shadow-lg"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    查看簽文
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};